import React, { useCallback, useEffect, useState } from 'react';
import { FilterGroup, FilterRule } from '../../types';
import { previewSegment } from '../../services/api';

// ── Field definitions ───────────────────────────────────────────────────────

const INDIA_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
  'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Delhi','Chandigarh','Jammu & Kashmir','Ladakh',
  'Puducherry',
];

const LEAD_STATUSES = [
  'new','ai_contacted','contacted','qualified','human_called','proposal','won','lost','nurture',
];

const BUSINESS_TYPES = [
  'Distributor','Retailer','Wholesaler','Hospital','Clinic','Pharmacy','C&F Agent',
];

type FieldDef = {
  key: string;
  label: string;
  type: 'text' | 'select' | 'multi_select' | 'boolean' | 'days_ago' | 'range' | 'json_contains' | 'number';
  options?: string[];
};

const SEGMENT_FIELDS: FieldDef[] = [
  { key: 'state',              label: 'State',              type: 'multi_select', options: INDIA_STATES },
  { key: 'city',               label: 'City',               type: 'text' },
  { key: 'status',             label: 'Lead Status',        type: 'multi_select', options: LEAD_STATUSES },
  { key: 'temperature',        label: 'Temperature',        type: 'select',       options: ['HOT', 'WARM', 'COLD'] },
  { key: 'wa_opted_in',        label: 'WA Opted-In',        type: 'boolean' },
  { key: 'business_type',      label: 'Business Type',      type: 'multi_select', options: BUSINESS_TYPES },
  { key: 'last_touch_at',      label: 'Last Contacted',     type: 'days_ago' },
  { key: 'mr_count',           label: 'MR Count',           type: 'range' },
  { key: 'specialty_interest', label: 'Products Interested',type: 'json_contains' },
  { key: 'score',              label: 'Score',              type: 'number' },
  { key: 'years_in_business',  label: 'Years in Business',  type: 'number' },
];

const OPS_FOR_TYPE: Record<string, { op: string; label: string }[]> = {
  text:          [{ op: 'eq', label: 'equals' }, { op: 'not_eq', label: 'not equals' }],
  select:        [{ op: 'eq', label: 'equals' }, { op: 'not_eq', label: 'not equals' }],
  multi_select:  [{ op: 'in', label: 'is one of' }, { op: 'not_in', label: 'is not' }],
  boolean:       [{ op: 'eq', label: 'equals' }],
  days_ago:      [{ op: 'gte', label: 'more than' }, { op: 'lte', label: 'less than' }],
  range:         [{ op: 'between', label: 'between' }, { op: 'gte', label: '>=' }, { op: 'lte', label: '<=' }],
  json_contains: [{ op: 'contains', label: 'contains' }],
  number:        [{ op: 'gte', label: '>=' }, { op: 'lte', label: '<=' }, { op: 'eq', label: 'equals' }, { op: 'between', label: 'between' }],
};

// ── FilterRuleRow ───────────────────────────────────────────────────────────

const FilterRuleRow: React.FC<{
  rule: FilterRule;
  onChange: (r: FilterRule) => void;
  onRemove: () => void;
}> = ({ rule, onChange, onRemove }) => {
  const fieldDef = SEGMENT_FIELDS.find(f => f.key === rule.field);
  const ops      = OPS_FOR_TYPE[fieldDef?.type ?? 'text'] ?? OPS_FOR_TYPE.text;

  const handleField = (key: string) => {
    const def = SEGMENT_FIELDS.find(f => f.key === key);
    const firstOp = (OPS_FOR_TYPE[def?.type ?? 'text'] ?? OPS_FOR_TYPE.text)[0].op;
    onChange({ field: key, op: firstOp as any, value: undefined });
  };

  const handleOp = (op: string) => onChange({ ...rule, op: op as any, value: undefined });

  const renderValue = () => {
    if (!fieldDef) return null;
    const { type, options } = fieldDef;

    if (type === 'multi_select') {
      const selected: string[] = Array.isArray(rule.value) ? rule.value : [];
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', flex: 1 }}>
          {selected.map(v => (
            <span key={v} style={{ background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: 20, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              {v}
              <button onClick={() => onChange({ ...rule, value: selected.filter(x => x !== v) })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9333ea', fontSize: 13, lineHeight: 1 }}>×</button>
            </span>
          ))}
          <select className="filter-select" style={{ height: 28, fontSize: 12 }}
            value="" onChange={e => e.target.value && onChange({ ...rule, value: [...selected, e.target.value] })}>
            <option value="">+ Add…</option>
            {(options ?? []).filter(o => !selected.includes(o)).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    }

    if (type === 'select') {
      return (
        <select className="filter-select" style={{ flex: 1, height: 32 }} value={rule.value ?? ''}
          onChange={e => onChange({ ...rule, value: e.target.value })}>
          <option value="">Select…</option>
          {(options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }

    if (type === 'boolean') {
      return (
        <select className="filter-select" style={{ flex: 1, height: 32 }} value={String(rule.value ?? true)}
          onChange={e => onChange({ ...rule, value: e.target.value === 'true' })}>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (type === 'days_ago') {
      const days = typeof rule.value === 'string' ? parseInt(rule.value) : (rule.value ?? 30);
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <input type="number" className="form-input" style={{ width: 70, height: 32 }} min={1} value={days}
            onChange={e => onChange({ ...rule, value: `${e.target.value}_days_ago` })} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>days ago</span>
        </div>
      );
    }

    if (type === 'range' && rule.op === 'between') {
      const vals: [number, number] = Array.isArray(rule.value) ? rule.value as [number, number] : [0, 100];
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <input type="number" className="form-input" style={{ width: 70, height: 32 }} value={vals[0]}
            onChange={e => onChange({ ...rule, value: [+e.target.value, vals[1]] })} />
          <span style={{ fontSize: 12 }}>—</span>
          <input type="number" className="form-input" style={{ width: 70, height: 32 }} value={vals[1]}
            onChange={e => onChange({ ...rule, value: [vals[0], +e.target.value] })} />
        </div>
      );
    }

    if (type === 'json_contains') {
      return (
        <input className="form-input" style={{ flex: 1, height: 32, fontSize: 13 }}
          placeholder="e.g. Azithromycin" value={rule.value ?? ''}
          onChange={e => onChange({ ...rule, value: e.target.value })} />
      );
    }

    // text / number / range with gte/lte
    return (
      <input type={type === 'number' || type === 'range' ? 'number' : 'text'}
        className="form-input" style={{ flex: 1, height: 32, fontSize: 13 }}
        value={rule.value ?? ''} onChange={e => onChange({ ...rule, value: type === 'number' || type === 'range' ? +e.target.value : e.target.value })} />
    );
  };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
      <select className="filter-select" style={{ width: 160, height: 32 }} value={rule.field} onChange={e => handleField(e.target.value)}>
        <option value="">Field…</option>
        {SEGMENT_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
      <select className="filter-select" style={{ width: 120, height: 32 }} value={rule.op} onChange={e => handleOp(e.target.value)}>
        {ops.map(o => <option key={o.op} value={o.op}>{o.label}</option>)}
      </select>
      {renderValue()}
      <button onClick={onRemove} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18, padding: '0 4px', flexShrink: 0, alignSelf: 'center' }}>×</button>
    </div>
  );
};

// ── ReachPreview ────────────────────────────────────────────────────────────

const ReachPreview: React.FC<{ filters: FilterGroup; entityType: string; channel: string }> = ({ filters, entityType, channel }) => {
  const [reach, setReach]     = useState<number | null>(null);
  const [waReach, setWaReach] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!filters.rules.length) { setReach(null); return; }
    setLoading(true);
    try {
      const r = await previewSegment(filters, entityType, channel);
      setReach(r.reach);
      setWaReach(r.wa_reach ?? null);
    } catch {} finally { setLoading(false); }
  }, [filters, entityType, channel]);

  // Debounce 600ms
  useEffect(() => {
    const t = setTimeout(refresh, 600);
    return () => clearTimeout(t);
  }, [refresh]);

  if (loading) return <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Counting…</div>;
  if (reach === null) return null;

  return (
    <div style={{ marginTop: 10, padding: '8px 14px', background: '#ede9fe', borderRadius: 8, fontSize: 13 }}>
      <strong style={{ color: '#6d28d9' }}>{reach.toLocaleString('en-IN')}</strong>
      <span style={{ color: '#7c3aed' }}> {entityType}s match this segment</span>
      {waReach !== null && channel === 'whatsapp' && (
        <span style={{ color: '#0369a1', marginLeft: 10 }}>
          &middot; <strong>{waReach.toLocaleString('en-IN')}</strong> WA-opted-in
        </span>
      )}
    </div>
  );
};

// ── FilterBuilder (exported) ────────────────────────────────────────────────

const FilterBuilder: React.FC<{
  value: FilterGroup;
  onChange: (f: FilterGroup) => void;
  entityType: string;
  channel: string;
}> = ({ value, onChange, entityType, channel }) => {
  const addRule = () => onChange({
    ...value,
    rules: [...value.rules, { field: SEGMENT_FIELDS[0].key, op: 'eq', value: undefined }],
  });

  const updateRule = (i: number, r: FilterRule) => onChange({
    ...value, rules: value.rules.map((x, idx) => idx === i ? r : x),
  });

  const removeRule = (i: number) => onChange({
    ...value, rules: value.rules.filter((_, idx) => idx !== i),
  });

  return (
    <div>
      {value.rules.map((rule, i) => (
        <FilterRuleRow key={i} rule={rule} onChange={r => updateRule(i, r)} onRemove={() => removeRule(i)} />
      ))}
      <button className="btn btn-ghost" style={{ fontSize: 13, marginTop: 4 }} onClick={addRule}>
        + Add filter
      </button>
      <ReachPreview filters={value} entityType={entityType} channel={channel} />
    </div>
  );
};

export default FilterBuilder;
export { ReachPreview };
